// -----------------------------------------------------------------------------
// UndoBanner
//
// Small pill that drops from the top of the guided checklist for 30 seconds
// after the attendance gate is dismissed.
//
// Accessibility:
//   role="status" + aria-live="polite" → screen reader announces on mount.
//   aria-label on Undo button clearly describes the action.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import Iconify from 'src/components/Iconify';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

export default function UndoBanner({ secondsLeft, onUndo, onDismiss }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [mounted, setMounted] = useState(false);

    // Trigger drop-from-top animation after first paint.
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 16);
        return () => clearTimeout(t);
    }, []);

    return (
        <Box
            data-testid="undo-banner"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2,
                py: 0.875,
                mb: 1.5,
                bgcolor: 'rgba(0, 171, 85, 0.10)',
                border: '1px solid rgba(0, 171, 85, 0.22)',
                borderRadius: '10px',
                // Drop from top animation
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'none' : 'translateY(-10px)',
                transition: reducedMotion ? 'none' : `opacity 200ms ${EASING}, transform 200ms ${EASING}`,
            }}
        >
            {/* Left — icon + text */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                <Iconify
                    icon="mdi:check-circle-outline"
                    width={15}
                    height={15}
                    sx={{ color: '#00AB55', flexShrink: 0 }}
                />
                <Typography
                    sx={{ fontSize: '13px', fontWeight: 500, color: '#1d1d1f', letterSpacing: '-0.01em' }}
                >
                    Marked attended
                    <Box
                        component="span"
                        data-testid="undo-banner-seconds"
                        sx={{ color: '#6e6e73', fontWeight: 400, ml: 0.75 }}
                    >
                        · {secondsLeft}s
                    </Box>
                </Typography>
            </Box>

            {/* Right — Undo + Dismiss */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    data-testid="undo-banner-undo-btn"
                    size="small"
                    aria-label="Undo marking this patient as attended"
                    onClick={onUndo}
                    sx={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#00AB55',
                        textTransform: 'none',
                        p: '2px 8px',
                        minWidth: 0,
                        borderRadius: '6px',
                        '&:hover': { bgcolor: 'rgba(0,171,85,0.10)' },
                    }}
                >
                    Undo
                </Button>
                <Button
                    data-testid="undo-banner-dismiss-btn"
                    size="small"
                    aria-label="Dismiss undo banner"
                    onClick={onDismiss}
                    sx={{
                        p: '2px 4px',
                        minWidth: 0,
                        color: '#6e6e73',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <Iconify icon="mdi:close" width={13} height={13} />
                </Button>
            </Box>
        </Box>
    );
}

UndoBanner.propTypes = {
    /** Current seconds remaining on the undo timer. */
    secondsLeft: PropTypes.number.isRequired,
    /** Called when the user clicks "Undo". */
    onUndo: PropTypes.func.isRequired,
    /** Called when the user clicks the × dismiss button. */
    onDismiss: PropTypes.func.isRequired,
};
